import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Card, Container, Divider, Grid2 as Grid, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import dayjs from 'dayjs'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React, { useEffect, useState } from 'react'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-coverflow'

import GroupSessionCard from '../components/cards/GroupSessionCard'
import UserCard from '../components/cards/UserCard'
import ImageGallery from '../components/common/ImageGallery'
import { TimeSlotsBookingCard } from '../components/forms/TimeSlotsBookingCard'
import SimpleHorizontalCards from '../components/layout/SimpleHorizontalCards'
import { availableSessionsByInstructorAtom, instructorAtom, selectedInstructorIdAtom } from '../contexts/AtomPrivateSessionSearch'
import { InstructorViewAtom } from '../contexts/StatePageScope'

// Mock data for instructor
const mockInstructor = {
  resourceId: 'dev-instructor-001',
  id: 'dev-instructor-001',
  avatar: 'https://img.bookingonsnow.com/images/xDw3E4oona/1768165551742-profile.png',
  firstName: 'John',
  lastName: 'Smith',
  name: 'John Smith',
  email: 'john.smith@example.com',
  roles: ['INSTRUCTOR'],
  bio: `# Professional Ski Instructor

拥有超过15年教学经验，能够教授从初学者到高级滑雪者的各个级别。

## 教学理念
- 关注个人学习风格
- 安全第一
- 渐进式技能发展
- 让学习变得有趣

## 专业认证
- CSIA Level 4 (Canadian Ski Instructors' Alliance)
- CASI Level 3 (Canadian Association of Snowboard Instructors)
- PSIA Level 3 (Professional Ski Instructors of America)

## 专长领域
- 高级滑雪技巧
- 竞速滑雪训练
- 自由式滑雪
- 儿童滑雪教学

> "学习的最佳方式是在享受乐趣的同时挑战自己！"

## 教学经验
我曾在多个世界知名的滑雪胜地工作，包括：
- Whistler Blackcomb, Canada
- Aspen Snowmass, USA
- Zermatt, Switzerland

我的学生包括从3岁的初学者到参加国际比赛的职业选手。`,
  sports: ['SKI', 'SNOWBOARD'],
  certificates: ['CSIA Level 1'],
  pictures: [
    {
      url: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
      r2Url: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
    },
    {
      url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop',
      r2Url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop',
    },
    {
      url: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&h=600&fit=crop',
      r2Url: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&h=600&fit=crop',
    },
    {
      url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop',
      r2Url: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop',
    },
  ],
  videos: [
    {
      url: 'https://img.bookingonsnow.com/videos/instructor-999/1768278752255-video-1768278750024-wl3b6e.mp4',
      r2Url: 'https://img.bookingonsnow.com/videos/instructor-999/1768278752255-video-1768278750024-wl3b6e.mp4',
      coverImageUrl: 'https://img.bookingonsnow.com/videos/instructor-999/1768278752255-cover-video-1768278750024-wl3b6e-cover.jpg',
      coverUrl: 'https://img.bookingonsnow.com/videos/instructor-999/1768278752255-cover-video-1768278750024-wl3b6e-cover.jpg',
    },
  ],
}

// Mock data for group sessions
const mockGroupSessions = [
  {
    resourceId: 'dev-group-session-001',
    id: 'dev-group-session-001',
    title: 'Beginner Ski Lesson - Morning Session',
    description: 'Perfect for first-time skiers! Learn the basics of skiing in a fun and supportive group environment.',
    date: dayjs().add(3, 'days').valueOf(),
    startTime: dayjs().add(3, 'days').hour(9).minute(0).valueOf(),
    endTime: dayjs().add(3, 'days').hour(11).minute(30).valueOf(),
    location: 'Whistler Mountain - Learning Zone',
    maxParticipants: 8,
    currentParticipants: 3,
    participants: ['user-001', 'user-002', 'user-003'],
    price: 89.99,
    currency: 'CAD',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    status: 'AVAILABLE',
    imageUrl: 'https://images.unsplash.com/photo-1551524164-6cf77f5e1d6e?w=800&h=600&fit=crop',
  },
  {
    resourceId: 'dev-group-session-002',
    id: 'dev-group-session-002',
    title: 'Intermediate Snowboard Techniques',
    description: 'Improve your snowboarding skills with advanced turning techniques and basic jumps.',
    date: dayjs().add(5, 'days').valueOf(),
    startTime: dayjs().add(5, 'days').hour(13).minute(0).valueOf(),
    endTime: dayjs().add(5, 'days').hour(15).minute(30).valueOf(),
    location: 'Blackcomb Mountain - Terrain Park',
    maxParticipants: 6,
    currentParticipants: 4,
    participants: ['user-004', 'user-005', 'user-006', 'user-007'],
    price: 129.99,
    currency: 'CAD',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    status: 'AVAILABLE',
    imageUrl: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
  },
  {
    resourceId: 'dev-group-session-003',
    id: 'dev-group-session-003',
    title: 'Advanced Ski Racing Clinic',
    description: 'For experienced skiers looking to improve speed and technique. Focus on racing fundamentals.',
    date: dayjs().add(7, 'days').valueOf(),
    startTime: dayjs().add(7, 'days').hour(10).minute(0).valueOf(),
    endTime: dayjs().add(7, 'days').hour(12).minute(0).valueOf(),
    location: 'Whistler Mountain - Race Course',
    maxParticipants: 4,
    currentParticipants: 2,
    participants: ['user-008', 'user-009'],
    price: 199.99,
    currency: 'CAD',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    status: 'AVAILABLE',
    imageUrl: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop',
  },
  {
    resourceId: 'dev-group-session-004',
    id: 'dev-group-session-004',
    title: 'Kids Ski Adventure (Ages 5-12)',
    description: 'Fun and safe skiing lessons designed specifically for children. Games and activities included!',
    date: dayjs().add(10, 'days').valueOf(),
    startTime: dayjs().add(10, 'days').hour(9).minute(30).valueOf(),
    endTime: dayjs().add(10, 'days').hour(11).minute(30).valueOf(),
    location: 'Whistler Mountain - Kids Zone',
    maxParticipants: 10,
    currentParticipants: 7,
    participants: ['user-010', 'user-011', 'user-012', 'user-013', 'user-014', 'user-015', 'user-016'],
    price: 79.99,
    currency: 'CAD',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    status: 'AVAILABLE',
    imageUrl: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&h=600&fit=crop',
  },
]

// Mock data for private sessions
const mockPrivateSessions = [
  {
    resourceId: 'dev-private-session-001',
    id: 'dev-private-session-001',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    date: dayjs().add(2, 'days').valueOf(),
    startTime: dayjs().add(2, 'days').hour(10).minute(0).valueOf(),
    endTime: dayjs().add(2, 'days').hour(12).minute(0).valueOf(),
    duration: 120,
    price: 199.99,
    currency: 'CAD',
    status: 'AVAILABLE',
  },
  {
    resourceId: 'dev-private-session-002',
    id: 'dev-private-session-002',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    date: dayjs().add(4, 'days').valueOf(),
    startTime: dayjs().add(4, 'days').hour(14).minute(0).valueOf(),
    endTime: dayjs().add(4, 'days').hour(16).minute(0).valueOf(),
    duration: 120,
    price: 199.99,
    currency: 'CAD',
    status: 'AVAILABLE',
  },
  {
    resourceId: 'dev-private-session-003',
    id: 'dev-private-session-003',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    date: dayjs().add(6, 'days').valueOf(),
    startTime: dayjs().add(6, 'days').hour(9).minute(0).valueOf(),
    endTime: dayjs().add(6, 'days').hour(11).minute(0).valueOf(),
    duration: 120,
    price: 199.99,
    currency: 'CAD',
    status: 'AVAILABLE',
  },
  {
    resourceId: 'dev-private-session-004',
    id: 'dev-private-session-004',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    date: dayjs().add(8, 'days').valueOf(),
    startTime: dayjs().add(8, 'days').hour(13).minute(0).valueOf(),
    endTime: dayjs().add(8, 'days').hour(15).minute(0).valueOf(),
    duration: 120,
    price: 199.99,
    currency: 'CAD',
    status: 'AVAILABLE',
  },
  {
    resourceId: 'dev-private-session-005',
    id: 'dev-private-session-005',
    instructorId: 'dev-instructor-001',
    instructor: mockInstructor,
    date: dayjs().add(9, 'days').valueOf(),
    startTime: dayjs().add(9, 'days').hour(10).minute(0).valueOf(),
    endTime: dayjs().add(9, 'days').hour(12).minute(0).valueOf(),
    duration: 120,
    price: 199.99,
    currency: 'CAD',
    status: 'AVAILABLE',
  },
]

const PrivateLessonsSection = ({ sx = {} }) => {
  const [expanded, setExpanded] = useState(true)

  const availableSessionsByInstructor = useAtomValue(availableSessionsByInstructorAtom)

  if (availableSessionsByInstructor?.length === 0) {
    return null
  }

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ alignItems: 'center', mt: 1, mb: 1 }}>
        <Button size="Large" color="primary" aria-expanded={expanded} aria-label={expanded ? 'Collapse sessions' : 'Expand sessions'} onClick={() => setExpanded(!expanded)}>
          <Typography variant="h5">Book a Private Lesson with this Instructor</Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Collapse in={expanded}>
        <Grid container justifyContent="center" alignItems="center" spacing={3}>
          <TimeSlotsBookingCard />
        </Grid>
      </Collapse>
    </Box>
  )
}

const GroupSessionsSection = ({ sessionChangeHook, sx = {} }) => {
  const groupSessions = useAtomValue(InstructorViewAtom.groupSessions)

  const [expanded, setExpanded] = useState(true)

  if (groupSessions.length === 0) {
    return null
  }

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ alignItems: 'center', mt: 1, mb: 1 }}>
        <Button size="Large" color="primary" aria-expanded={expanded} aria-label={expanded ? 'Collapse sessions' : 'Expand sessions'} onClick={() => setExpanded(!expanded)}>
          <Typography variant="h5"> Group Sessions by this Instructor</Typography>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Collapse in={expanded}>
        <SimpleHorizontalCards>
          {groupSessions.map((session) => {
            return <GroupSessionCard key={session.resourceId} session={session} mode="card" updateHook={sessionChangeHook} deleteHook={sessionChangeHook} />
          })}
        </SimpleHorizontalCards>
      </Collapse>
    </Box>
  )
}

const InstructorCard = () => {
  const instructor = useAtomValue(instructorAtom)
  return <UserCard user={instructor} clickable={false} collapsibleBio={true} shareButton={true} chatButton={false} size="large" sx={{ mt: 0, border: 'none', '&:hover': {} }} />
}

/**
 * Dev Instructor View Page - 展示教练详情页面的示例
 * 使用模拟数据来演示 InstructorView 页面的完整功能
 */
export default function DevInstructor() {
  const [instructor, setInstructor] = useAtom(instructorAtom)
  const [groupSessions, setGroupSessions] = useAtom(InstructorViewAtom.groupSessions)
  // const setAvailableSessionsState = useSetAtom(availableSessionsByInstructorAtom)
  const setSelectedInstructorId = useSetAtom(selectedInstructorIdAtom)

  // Load mock data on mount
  useEffect(() => {
    setInstructor(mockInstructor)
    setGroupSessions(mockGroupSessions)
    // setAvailableSessionsState(mockPrivateSessions)
    setSelectedInstructorId(mockInstructor.resourceId)
  }, [setInstructor, setGroupSessions, setSelectedInstructorId])

  // Combine pictures and videos into a unified media array
  const media = React.useMemo(() => {
    const pictureMedia = (instructor?.pictures || []).map((picture) => ({
      type: 'image',
      url: picture.url || picture.r2Url,
    }))

    const videoMedia = (instructor?.videos || []).map((video) => ({
      type: 'video',
      url: video.url || video.r2Url,
      coverUrl: video.coverImageUrl || video.coverUrl,
    }))

    return [...pictureMedia, ...videoMedia]
  }, [instructor?.pictures, instructor?.videos])

  const reload = React.useCallback(() => {
    console.log('Reload called - refreshing mock data')
    setGroupSessions([...mockGroupSessions])
    // setAvailableSessionsState([...mockPrivateSessions])
  }, [setGroupSessions])

  if (!instructor) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4 }}>
          <Typography align="center">Loading mock instructor data...</Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.contrastText">
          <strong>开发模式：</strong> 这是使用模拟数据展示的 Instructor View 页面示例。所有数据都是模拟的，用于开发和测试目的。
        </Typography>
      </Box>

      <Card>
        <InstructorCard />

        {/* Public Media Gallery with Swiper */}
        {media.length > 0 && (
          <ImageGallery
            media={media}
            title="Public Media Gallery"
            collapsible={true}
            defaultExpanded={true}
            autoplay={true}
            autoplayDelay={3000}
            effect="coverflow"
            sx={{ mt: 2 }}
          />
        )}
      </Card>
    </Container>
  )
}
